/**
 * The Versant setup callables (functions/src/integrations) — the
 * Integrations page's Versant rows and setup popup talk to these only; no
 * login state is readable from Firestore by the client.
 */
import { httpsCallable } from 'firebase/functions'
import { functions } from '../config'
import type { VersantLoginState } from '../../shared/versantLogin'

export interface VersantLoginListing {
  /** Each clinic holding a Versant record, and its login's state. */
  clinics: Array<{ clinicId: string; loginId: string; address: string; state: VersantLoginState }>
  /** The practice's Versant addresses, for the reuse list. */
  logins: Array<{ loginId: string; address: string; state: VersantLoginState }>
  /** The codes domain the practice holds, or null while it holds none. */
  domain: string | null
}

/** The practice's Versant logins (admins, staff in view mode too). */
export async function listVersantLogins(orgId: string): Promise<VersantLoginListing> {
  const fn = httpsCallable<{ orgId: string }, VersantLoginListing>(functions, 'listVersantLogins')
  return (await fn({ orgId })).data
}

export type OpenVersantSetupResult =
  | { address: string; loginId: string; state: VersantLoginState }
  | { noDomain: true }

/** A new login from a mailbox name, or one of the practice's reused. A
 *  refused name rejects with the sentence to show. */
export async function openVersantSetup(
  orgId: string,
  clinicId: string,
  pick: { mailboxName: string } | { loginId: string },
): Promise<OpenVersantSetupResult> {
  const fn = httpsCallable<Record<string, string>, OpenVersantSetupResult>(functions, 'openVersantSetup')
  return (await fn({ orgId, clinicId, ...pick })).data
}

/** Save the clinic's login's password; resolves with the login's state after it. */
export async function saveVersantPassword(orgId: string, clinicId: string, password: string): Promise<VersantLoginState> {
  const fn = httpsCallable<{ orgId: string; clinicId: string; password: string }, { state: VersantLoginState }>(
    functions, 'saveVersantPassword',
  )
  return (await fn({ orgId, clinicId, password })).data.state
}

/** Unlink the clinic from its Versant login; the login stays for the practice. */
export async function removeVersantClinic(orgId: string, clinicId: string): Promise<void> {
  const fn = httpsCallable<{ orgId: string; clinicId: string }, { removed: boolean }>(functions, 'removeVersantClinic')
  await fn({ orgId, clinicId })
}
